import React, { useState, useEffect } from 'react';
import { Modal, Button, Form, Row, Col, Badge, Alert, Spinner } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import { actions as aprobacionActions, selectors as aprobacionSelectors } from '../../reducers/aprobaciones';
import { getModuloLabel, getSnapshotResumen } from './moduloLabels';

const SolicitarAprobacionModal = ({
    show,
    onHide,
    proyectoId,
    moduloTipo,
    referenciaId = null,
    tituloDefault = '',
    snapshotData = null,
    onSuccess,
}) => {
    const dispatch = useDispatch();
    const candidatos = useSelector(aprobacionSelectors.getCandidatos);
    const isLoading = useSelector(aprobacionSelectors.getIsLoading);

    const [titulo, setTitulo] = useState('');
    const [descripcion, setDescripcion] = useState('');
    const [selectedFirmantes, setSelectedFirmantes] = useState([]);
    const [formError, setFormError] = useState('');

    const armarTituloDefault = () => {
        const label = getModuloLabel(moduloTipo);
        const resumen = getSnapshotResumen(snapshotData);
        const base = `Aprobación de ${label}`;
        return resumen ? `${base}: ${resumen}` : base;
    };

    useEffect(() => {
        if (show && proyectoId) {
            dispatch(aprobacionActions.getCandidatos(proyectoId));
            setTitulo(tituloDefault || armarTituloDefault());
            setDescripcion('');
            setSelectedFirmantes([]);
            setFormError('');
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [show, proyectoId, moduloTipo, tituloDefault]);

    const toggleFirmante = (usuarioId) => {
        setSelectedFirmantes(prev => {
            if (prev.includes(usuarioId)) {
                return prev.filter(id => id !== usuarioId);
            } else {
                return [...prev, usuarioId];
            }
        });
    };

    const handleSelectAllAptos = () => {
        const aptosIds = (candidatos || [])
            .filter(c => c.aptoParaAprobar && c.usuarioId)
            .map(c => c.usuarioId);

        if (selectedFirmantes.length === aptosIds.length) {
            setSelectedFirmantes([]);
        } else {
            setSelectedFirmantes(aptosIds);
        }
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        setFormError('');

        if (!titulo.trim()) {
            setFormError('Debe ingresar un título para la solicitud.');
            return;
        }

        if (selectedFirmantes.length === 0) {
            setFormError('Debe seleccionar al menos un aprobador con licencia profesional.');
            return;
        }

        const payload = {
            moduloTipo,
            referenciaId,
            titulo: titulo.trim(),
            descripcion: descripcion.trim(),
            firmantesIds: selectedFirmantes,
            snapshotData,
        };

        dispatch(aprobacionActions.solicitarAprobacion(proyectoId, payload, (data) => {
            if (onSuccess) onSuccess(data);
            onHide();
        }));
    };

    return (
        <Modal show={show} onHide={onHide} size="lg" centered backdrop="static">
            <Modal.Header closeButton>
                <Modal.Title className="d-flex align-items-center">
                    <i className="bi bi-send-check text-primary me-2"></i>
                    Enviar a Flujo de Aprobación
                </Modal.Title>
            </Modal.Header>
            <Form onSubmit={handleSubmit}>
                <Modal.Body className="bg-light">
                    <Alert variant="info" className="d-flex align-items-center py-2">
                        <i className="bi bi-info-circle-fill fs-4 me-3"></i>
                        <div>
                            <strong>Modo Bloqueado:</strong> Al enviar a aprobación, esta sección quedará congelada en modo solo lectura hasta que todos los firmantes emitan su voto.
                        </div>
                    </Alert>

                    <Alert variant="light" className="border d-flex align-items-start py-2">
                        <i className="bi bi-bullseye fs-4 me-3 mt-1"></i>
                        <div className="w-100">
                            <div className="fw-bold mb-1">Elemento a aprobar</div>
                            <div>
                                <Badge variant="primary" className="mr-2">{getModuloLabel(moduloTipo)}</Badge>
                                {referenciaId ? (
                                    <Badge variant="secondary">Referencia: #{referenciaId}</Badge>
                                ) : null}
                            </div>
                            {getSnapshotResumen(snapshotData) && (
                                <div className="mt-1 small">
                                    <strong>Documento:</strong> {getSnapshotResumen(snapshotData)}
                                </div>
                            )}
                            {!referenciaId && !getSnapshotResumen(snapshotData) && (
                                <div className="mt-1 small text-muted">
                                    Solicitud general sin elemento vinculado. Especifique en el título qué desea que se apruebe.
                                </div>
                            )}
                        </div>
                    </Alert>

                    {formError && <Alert variant="danger">{formError}</Alert>}

                    <Form.Group className="mb-3">
                        <Form.Label className="fw-bold">Título de la Solicitud</Form.Label>
                        <Form.Control
                            type="text"
                            required
                            placeholder="Ej: Aprobación de Acta de Constitución v1.0"
                            value={titulo}
                            onChange={e => setTitulo(e.target.value)}
                        />
                    </Form.Group>

                    <Form.Group className="mb-4">
                        <Form.Label className="fw-bold">Instrucciones / Observaciones para los aprobadores (Opcional)</Form.Label>
                        <Form.Control
                            as="textarea"
                            rows={3}
                            placeholder="Indique puntos clave a revisar o contexto adicional..."
                            value={descripcion}
                            onChange={e => setDescripcion(e.target.value)}
                        />
                    </Form.Group>

                    <div className="p-3 bg-white border rounded shadow-sm">
                        <div className="d-flex justify-content-between align-items-center mb-3">
                            <h6 className="fw-bold text-primary mb-0">
                                <i className="bi bi-people me-2"></i>Seleccionar Aprobadores (Interesados con Licencia Profesional)
                            </h6>
                            <Button
                                variant="outline-secondary"
                                size="sm"
                                onClick={handleSelectAllAptos}
                            >
                                Seleccionar Todos los Aptos
                            </Button>
                        </div>

                        {isLoading ? (
                            <div className="text-center py-4">
                                <Spinner animation="border" variant="primary" />
                                <span className="ms-2">Cargando interesados del proyecto...</span>
                            </div>
                        ) : (
                            <div className="table-responsive" style={{ maxHeight: '250px', overflowY: 'auto' }}>
                                <table className="table table-hover align-middle mb-0">
                                    <thead className="table-light">
                                        <tr>
                                            <th style={{ width: '40px' }}></th>
                                            <th>Nombre / Interesado</th>
                                            <th>Correo Electrónico</th>
                                            <th>Rol / Cargo</th>
                                            <th>Estado Licencia</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {(candidatos || []).map((cand, idx) => (
                                            <tr key={cand.interesadoId || idx} className={!cand.aptoParaAprobar ? 'text-muted bg-light' : ''}>
                                                <td>
                                                    <Form.Check
                                                        type="checkbox"
                                                        disabled={!cand.aptoParaAprobar}
                                                        checked={cand.usuarioId ? selectedFirmantes.includes(cand.usuarioId) : false}
                                                        onChange={() => cand.usuarioId && toggleFirmante(cand.usuarioId)}
                                                    />
                                                </td>
                                                <td>
                                                    <strong>{cand.nombre}</strong>
                                                </td>
                                                <td>
                                                    <small>{cand.email}</small>
                                                </td>
                                                <td>
                                                    <small className="text-muted">{cand.cargo || cand.rol || 'Interesado'}</small>
                                                </td>
                                                <td>
                                                    {cand.aptoParaAprobar ? (
                                                        <Badge variant="success">
                                                            <i className="bi bi-check-circle mr-1"></i>Apto (Licencia Pro)
                                                        </Badge>
                                                    ) : !cand.tieneUsuario ? (
                                                        <Badge variant="secondary" title="El correo no corresponde a un usuario registrado de GORU">
                                                            Sin Usuario GORU
                                                        </Badge>
                                                    ) : (
                                                        <Badge variant="warning" title="El usuario no tiene una Licencia Profesional o Corporativa">
                                                            Sin Licencia Pro
                                                        </Badge>
                                                    )}
                                                </td>
                                            </tr>
                                        ))}

                                        {(!candidatos || candidatos.length === 0) && (
                                            <tr>
                                                <td colSpan="5" className="text-center text-muted py-3">
                                                    No hay interesados registrados en este proyecto con usuario de GORU.
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        )}
                        <small className="text-muted d-block mt-2">
                            * Solo los interesados que cuenten con usuario registrado en GORU y <strong>Licencia Profesional</strong> activa pueden ser asignados como firmantes.
                        </small>
                    </div>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={onHide} disabled={isLoading}>
                        Cancelar
                    </Button>
                    <Button variant="primary" type="submit" disabled={isLoading || selectedFirmantes.length === 0}>
                        {isLoading ? <Spinner animation="border" size="sm" /> : <i className="bi bi-send me-1"></i>}
                        Enviar a Aprobación ({selectedFirmantes.length})
                    </Button>
                </Modal.Footer>
            </Form>
        </Modal>
    );
};

export default SolicitarAprobacionModal;

